import { useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, Eye } from "lucide-react";
import { BACKEND_URL } from "@/constants/api";
import { REPORT_TYPES } from "@/constants/reportTypes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Modal } from "@/components/modals/Modal";

// Returns { label, subtitle } for a given reportType value, falling back gracefully.
function getTypeDisplay(value) {
  const found = REPORT_TYPES.find((t) => t.value === value);
  if (found) return `${found.label} · ${found.subtitle}`;
  return value;
}

export function JoinSchoolModal({ open, onClose }) {
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [inviteCode, setInviteCode] = useState("");
  // "code" | "confirm"
  const [step, setStep] = useState("code");
  const [classes, setClasses] = useState([]);
  const [checking, setChecking] = useState(false);
  const [joining, setJoining] = useState(false);

  const handleClose = () => {
    setInviteCode("");
    setStep("code");
    setClasses([]);
    onClose();
  };

  // Step 1: validate the code input and fetch the user's classes.
  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    const code = inviteCode.trim();
    if (!code) return;

    setChecking(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/report-groups`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        // If we can't fetch classes, skip disclosure and join directly.
        await performJoin(code, token);
        return;
      }

      const data = await res.json().catch(() => []);
      const ownedClasses = Array.isArray(data) ? data : [];

      if (ownedClasses.length === 0) {
        // No existing classes - join without disclosure.
        await performJoin(code, token);
      } else {
        setClasses(ownedClasses);
        setStep("confirm");
      }
    } catch {
      // Fetch error - skip disclosure and join directly.
      try {
        const token = await getToken();
        await performJoin(inviteCode.trim(), token);
      } catch (joinErr) {
        toast.error(joinErr.message);
      }
    } finally {
      setChecking(false);
    }
  };

  // Step 2: user confirmed - proceed with the actual join.
  const handleConfirmJoin = async () => {
    setJoining(true);
    try {
      const token = await getToken();
      await performJoin(inviteCode.trim(), token);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setJoining(false);
    }
  };

  // Shared join logic - throws on failure.
  const performJoin = async (code, token) => {
    const res = await fetch(`${BACKEND_URL}api/schools/join`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ inviteCode: code }),
    });

    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.message || "Failed to join school");

    toast.success(`Joined ${body.school?.name || "school"} successfully!`);
    handleClose();
    navigate("/schools");
  };

  const isBusy = checking || joining;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Join a School"
      subtitle={
        step === "code"
          ? "Enter the invite code shared by your school admin."
          : "Review what will be shared before joining."
      }
      icon={<Users size={16} className="text-primary" />}
      preventClose={isBusy}
    >
      {step === "code" && (
        <form onSubmit={handleCodeSubmit} className="px-6 py-5 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">
              Invite Code <span className="text-destructive/80 font-normal">*</span>
            </Label>
            <Input
              placeholder="e.g. aB3kX9mQ2z"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              required
              className="h-10 font-mono tracking-wider"
              autoComplete="off"
              spellCheck={false}
            />
            <p className="text-xs text-muted-foreground">Ask your school admin for the invite code.</p>
          </div>

          <div className="flex gap-2 pt-1">
            <Button type="submit" disabled={isBusy || !inviteCode.trim()} className="flex-1">
              {checking ? "Checking..." : "Join School"}
            </Button>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isBusy}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {step === "confirm" && (
        <div className="px-6 py-5 space-y-4">
          {/* Disclosure notice */}
          <div className="rounded-xl border border-border bg-muted/30 px-4 py-3.5">
            <div className="flex items-start gap-3">
              <Eye size={15} className="text-primary mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground leading-snug">
                  Your classes will be visible to the school admin
                </p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  By joining this school, the following classes will become part of the school
                  and visible to the school admin:
                </p>
              </div>
            </div>
          </div>

          {/* Class list */}
          <ul className="space-y-2 max-h-48 overflow-y-auto pr-1">
            {classes.map((cls) => (
              <li
                key={cls._id}
                className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2.5"
              >
                <span className="text-sm font-medium text-foreground truncate">{cls.name}</span>
                <span className="text-xs text-muted-foreground ml-3 shrink-0">
                  {getTypeDisplay(cls.reportType)}
                </span>
              </li>
            ))}
          </ul>

          <div className="flex gap-2 pt-1">
            <Button onClick={handleConfirmJoin} disabled={joining} className="flex-1">
              {joining ? "Joining..." : "Join School"}
            </Button>
            <Button type="button" variant="outline" onClick={handleClose} disabled={joining}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
